'use client';

import { useAuth } from '@/backend/context';
import { useOwner } from '@/backend/firebase/hooks';
import { useStorage } from '@/backend/firebase/hooks/useStorage';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { PageHeader } from '@/components/ui/PageHeader';
import { cn } from '@/lib/utils';
import {
  normalizeCatalogSections,
  type CatalogItem,
  type CatalogSection,
} from '@/shared/schema';
import {
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  Crop,
  FolderOpen,
  GripVertical,
  ImageIcon,
  Images,
  Instagram,
  LayoutGrid,
  Link2,
  Loader2,
  Plus,
  Share2,
  SplitSquareHorizontal,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

// NUESTRO COMPONENTE GUARDIA
import { FormGuard } from '@/components/form-guard';
import { ImageCropperModal } from '@/components/image-cropper-modal';
import { IMAGE_ASPECT_CLASSES, IMAGE_RATIOS } from '@/shared/utils';

const cleanExternalUrl = (url: string) => {
  try {
    const urlObj = new URL(url);
    // Limpiamos parámetros de rastreo (utms, igsh, etc)
    urlObj.search = '';
    return urlObj.toString();
  } catch {
    return url; // Si no es una URL válida aún, la devolvemos tal cual
  }
};

const getAutoLabel = (url: string, currentLabel: string) => {
  // Solo auto-etiquetamos si el label es el default o está vacío
  if (currentLabel !== 'Nuevo Enlace' && currentLabel !== '')
    return currentLabel;

  if (url.includes('instagram.com/reel')) return 'Ver Reel en Instagram';
  if (url.includes('instagram.com/p/')) return 'Ver Post en Instagram';
  if (url.includes('facebook.com')) return 'Ver en Facebook';
  if (url.includes('youtube.com') || url.includes('youtu.be'))
    return 'Ver Video en YouTube';
  return currentLabel;
};

const generateId = () => Math.random().toString(36).substring(2, 9);

// Haptic feedback helper
const triggerHaptic = () => {
  if (navigator.vibrate) navigator.vibrate(50);
};

export default function CatalogPage() {
  const [isSaving, setIsSaving] = useState(false);
  const ownerId = useAuth((s) => s.user?.uid);
  const { data: profile, isLoading: isProfileLoading } =
    useOwner.getById(ownerId);
  const profileMutation = useOwner.updatePublic();
  const uploadMutation = useStorage.useUploadBranding();

  const [catalog, setCatalog] = useState<CatalogSection[]>([]);
  const [initialCatalog, setInitialCatalog] = useState<CatalogSection[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Trackear múltiples subidas
  const [activeUploads, setActiveUploads] = useState<Set<string>>(new Set());

  // Trackear imagen activa para edición (tap-to-reveal)
  const [activeImageEdit, setActiveImageEdit] = useState<string | null>(null);

  const [croppingQueue, setCroppingQueue] = useState<
    {
      src: string;
      sectionId: string;
      targetItemId?: string;
      targetSlot?: 'left' | 'right';
    }[]
  >([]);
  const [totalToCrop, setTotalToCrop] = useState(0);

  // --- CROPPER STATE ---
  const [cropper, setCropper] = useState<{
    isOpen: boolean;
    imageSrc: string | null;
    aspectRatio: number;
    sectionId: string;
    targetItemId?: string;
    targetSlot?: 'left' | 'right';
  }>({
    isOpen: false,
    imageSrc: null,
    aspectRatio: IMAGE_RATIOS.PRODUCT,
    sectionId: '',
  });

  // 1. Inicialización de datos
  useEffect(() => {
    if (profile?.public?.catalog) {
      const normalized = normalizeCatalogSections(profile.public.catalog);
      setCatalog(normalized);
      setInitialCatalog(normalized); // Guardamos la foto inicial para comparar
      setHasUnsavedChanges(false);
    }
  }, [profile]);

  const updateCatalog = (next: React.SetStateAction<CatalogSection[]>) => {
    setHasUnsavedChanges(true);
    setCatalog(next);
  };

  // 2. Control de Estado Sucio (Dirty)
  const isDirty = useMemo(() => {
    return (
      hasUnsavedChanges ||
      JSON.stringify(catalog) !== JSON.stringify(initialCatalog)
    );
  }, [catalog, hasUnsavedChanges, initialCatalog]);

  // 3. Guardado Explícito
  const handleSaveAll = async () => {
    if (!ownerId) return;
    if (activeUploads.size > 0 || croppingQueue.length > 0 || cropper.isOpen) {
      toast.error('Espera a que terminen los recortes y subidas');
      return;
    }

    setIsSaving(true);
    try {
      const normalizedCatalog = normalizeCatalogSections(catalog);
      await profileMutation.mutateAsync({ catalog: normalizedCatalog });
      setCatalog(normalizedCatalog);
      setInitialCatalog(normalizedCatalog); // Reseteamos el estado dirty
      setHasUnsavedChanges(false);
      toast.success('Catálogo publicado exitosamente');
    } catch (error) {
      toast.error('Error al sincronizar cambios');
    } finally {
      setIsSaving(false);
    }
  };

  // --- HANDLERS: SECTIONS ---
  const addSection = () => {
    const newSection: CatalogSection = {
      id: generateId(),
      title: 'Nueva Sección',
      type: 'grid',
      images: [],
      links: [], // Added initialization
      isActive: true,
      order: catalog.length,
      comparisonLabels: { left: 'Antes', right: 'Después' },
    };
    const newCatalog = [...catalog, newSection];
    updateCatalog(newCatalog);

    // Scroll to new section and focus title input
    setTimeout(() => {
      const sectionElement = document.getElementById(
        `section-${newSection.id}`,
      );
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the title input after scroll
        setTimeout(() => {
          const titleInput = sectionElement.querySelector(
            'input[placeholder="NOMBRE DEL ÁLBUM"]',
          ) as HTMLInputElement;
          if (titleInput) {
            titleInput.focus();
            titleInput.select();
          }
        }, 300);
      }
    }, 100);
  };

  const removeSection = (sectionId: string) => {
    updateCatalog((prev) => prev.filter((s) => s.id !== sectionId));
  };

  const updateSection = (
    sectionId: string,
    updates: Partial<CatalogSection>,
  ) => {
    updateCatalog((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, ...updates } : s)),
    );
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === catalog.length - 1) return;

    updateCatalog((prev) => {
      const newCat = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [newCat[index], newCat[targetIndex]] = [
        newCat[targetIndex],
        newCat[index],
      ];
      return newCat.map((s, i) => ({ ...s, order: i }));
    });
  };

  // --- HANDLERS: IMAGES ---
  const moveImage = (
    sectionId: string,
    imgIndex: number,
    direction: 'left' | 'right',
  ) => {
    updateCatalog((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        if (direction === 'left' && imgIndex === 0) return s;
        if (direction === 'right' && imgIndex === s.images.length - 1) return s;

        const newImages = [...s.images];
        const targetIndex = direction === 'left' ? imgIndex - 1 : imgIndex + 1;
        [newImages[imgIndex], newImages[targetIndex]] = [
          newImages[targetIndex],
          newImages[imgIndex],
        ];
        return {
          ...s,
          images: newImages.map((img, i) => ({ ...img, order: i })),
        };
      }),
    );
  };

  const swapComparisonImages = (sectionId: string, imageId: string) => {
    updateCatalog((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          images: s.images.map((img) => {
            if (img.id !== imageId) return img;
            return {
              ...img,
              url: img.beforeUrl || '',
              beforeUrl: img.url || null,
            };
          }),
        };
      }),
    );
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    sectionId: string,
    targetItemId?: string,
    targetSlot?: 'left' | 'right',
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !ownerId) return;

    const section = catalog.find((s) => s.id === sectionId);
    const isComparison = section?.type === 'comparison';

    const readFile = (file: File): Promise<string> => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    };

    // Pre-generar IDs para pares si estamos en modo "añadir nuevos" en comparación
    const pairIds =
      isComparison && !targetItemId
        ? Array.from({ length: Math.ceil(files.length / 2) }, () =>
            generateId(),
          )
        : [];

    const newItems = await Promise.all(
      files.map(async (file, i) => {
        let itemSlot = targetSlot;
        let itemId = targetItemId;

        if (isComparison && !targetItemId) {
          // Alternar: i=0 (Before), i=1 (After), i=2 (Before)...
          itemSlot = i % 2 === 0 ? 'left' : 'right';
          itemId = pairIds[Math.floor(i / 2)];
        }

        return {
          src: await readFile(file),
          sectionId,
          targetItemId: itemId,
          targetSlot: itemSlot,
        };
      }),
    );

    setTotalToCrop(newItems.length);
    setCroppingQueue(newItems);

    // Iniciar con la primera imagen
    const first = newItems[0];
    setCropper({
      isOpen: true,
      imageSrc: first.src,
      aspectRatio: IMAGE_RATIOS.PRODUCT,
      sectionId,
      targetItemId: first.targetItemId,
      targetSlot: first.targetSlot,
    });

    // Reset input
    e.target.value = '';
  };

  const onCropComplete = async (croppedFile: File) => {
    if (!ownerId || !cropper.sectionId) return;

    const currentSectionId = cropper.sectionId;
    const currentTargetItemId = cropper.targetItemId;
    const currentTargetSlot = cropper.targetSlot;

    const uploadKey = currentTargetItemId
      ? `${currentTargetItemId}-${currentTargetSlot}`
      : `sec-${currentSectionId}-${generateId()}`;

    // 1. Iniciar subida en "background"
    const uploadProcess = async () => {
      setActiveUploads((prev) => new Set(prev).add(uploadKey));
      try {
        const url = await uploadMutation.mutateAsync({
          file: croppedFile,
          ownerId,
          type: 'catalog',
          ratioType: 'PRODUCT',
        });

        updateCatalog((prev) =>
          prev.map((s) => {
            if (s.id !== currentSectionId) return s;

            const existingItem = s.images.find(
              (img) => img.id === currentTargetItemId,
            );

            if (existingItem) {
              return {
                ...s,
                images: s.images.map((img) =>
                  img.id === currentTargetItemId
                    ? {
                        ...img,
                        [currentTargetSlot === 'left' ? 'beforeUrl' : 'url']:
                          url,
                      }
                    : img,
                ),
              };
            }

            const newItem: CatalogItem = {
              id: currentTargetItemId || generateId(),
              url: currentTargetSlot === 'left' ? '' : url,
              beforeUrl: currentTargetSlot === 'left' ? url : null,
              order: s.images.length,
            };
            return { ...s, images: [...s.images, newItem] };
          }),
        );
        toast.success('Imagen cargada');
      } catch (error) {
        toast.error('Error al subir imagen');
      } finally {
        setActiveUploads((prev) => {
          const next = new Set(prev);
          next.delete(uploadKey);
          return next;
        });
      }
    };

    // Lanzamos el proceso de subida sin awaitar
    uploadProcess();

    // 2. Avanzar inmediatamente a la siguiente imagen si existe
    setCroppingQueue((prev) => {
      const nextQueue = prev.slice(1);
      if (nextQueue.length > 0) {
        const next = nextQueue[0];
        setCropper({
          isOpen: true,
          imageSrc: next.src,
          aspectRatio: IMAGE_RATIOS.PRODUCT,
          sectionId: next.sectionId,
          targetItemId: next.targetItemId,
          targetSlot: next.targetSlot,
        });
      } else {
        setTotalToCrop(0);
        setCropper((c) => ({ ...c, isOpen: false }));
      }
      return nextQueue;
    });
  };

  const removeImageSlot = (
    sectionId: string,
    imageId: string,
    slot: 'left' | 'right' | 'both',
  ) => {
    updateCatalog((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        if (slot === 'both')
          return { ...s, images: s.images.filter((img) => img.id !== imageId) };
        return {
          ...s,
          images: s.images.map((img) => {
            if (img.id !== imageId) return img;
            if (slot === 'left') return { ...img, beforeUrl: null };
            if (slot === 'right') return { ...img, url: '' };
            return img;
          }),
        };
      }),
    );
  };

  const updateImage = (
    sectionId: string,
    imageId: string,
    updates: Partial<CatalogItem>,
  ) => {
    updateCatalog((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              images: s.images.map((img) =>
                img.id === imageId ? { ...img, ...updates } : img,
              ),
            }
          : s,
      ),
    );
  };

  // --- HANDLERS: LINKS ---
  const addLink = (sectionId: string) => {
    updateCatalog((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        const newLink = {
          id: generateId(),
          label: 'Nuevo Enlace',
          url: 'https://',
          order: s.links?.length || 0,
        };
        return { ...s, links: [...(s.links || []), newLink] };
      }),
    );
  };

  const updateLink = (
    sectionId: string,
    linkId: string,
    updates: Partial<{ label: string; url: string }>,
  ) => {
    updateCatalog((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          links: s.links?.map((l) => {
            if (l.id !== linkId) return l;

            let newUrl = updates.url !== undefined ? updates.url : l.url;
            let newLabel =
              updates.label !== undefined ? updates.label : l.label;

            // Si se está actualizando la URL, aplicamos la magia
            if (updates.url) {
              newUrl = cleanExternalUrl(updates.url);
              newLabel = getAutoLabel(newUrl, newLabel);
            }

            return { ...l, url: newUrl, label: newLabel };
          }),
        };
      }),
    );
  };

  const removeLink = (sectionId: string, linkId: string) => {
    updateCatalog((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? { ...s, links: s.links?.filter((l) => l.id !== linkId) }
          : s,
      ),
    );
  };

  const moveLink = (
    sectionId: string,
    linkIndex: number,
    direction: 'up' | 'down',
  ) => {
    updateCatalog((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId || !s.links) return s;
        if (direction === 'up' && linkIndex === 0) return s;
        if (direction === 'down' && linkIndex === s.links.length - 1) return s;

        const newLinks = [...s.links];
        const targetIndex = direction === 'up' ? linkIndex - 1 : linkIndex + 1;
        [newLinks[linkIndex], newLinks[targetIndex]] = [
          newLinks[targetIndex],
          newLinks[linkIndex],
        ];
        return {
          ...s,
          links: newLinks.map((l, i) => ({ ...l, order: i })),
        };
      }),
    );
  };

  if (isProfileLoading) {
    return (
      <div className="bg-background flex h-screen items-center justify-center">
        <Loader2 className="text-ring h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isUploadBusy =
    activeUploads.size > 0 || croppingQueue.length > 0 || cropper.isOpen;

  return (
    <div className="bg-background animate-in fade-in min-h-screen pb-32 duration-500">
      <main className="mx-auto max-w-md space-y-6 px-4 pt-6 sm:max-w-2xl">
        <PageHeader title="Galería" subtitle="Catálogos" showBack={true} />
        {(isUploadBusy || isDirty) && (
          <div className="border-border bg-secondary/20 rounded-2xl border px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground text-[10px] font-black tracking-widest uppercase">
                {isUploadBusy
                  ? activeUploads.size > 0
                    ? `Subiendo ${activeUploads.size} imagen${activeUploads.size === 1 ? '' : 'es'}`
                    : 'Recorte en progreso'
                  : 'Cambios sin publicar'}
              </span>
              {isUploadBusy && (
                <Loader2 className="text-ring h-4 w-4 shrink-0 animate-spin" />
              )}
              {isDirty && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveAll}
                  disabled={isSaving}
                  className="h-9 shrink-0 rounded-xl px-4 text-[10px] font-black tracking-widest uppercase"
                >
                  {isSaving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    'Publicar'
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
        {/* BOTÓN CREAR ÁLBUM - STICKY BOTTOM */}
        <div
          className={cn(
            'fixed right-0 left-0 z-40 px-4 lg:right-6 lg:left-auto lg:px-0',
            isDirty ? 'bottom-30' : 'bottom-15',
            'lg:bottom-6',
          )}
        >
          <div className="flex items-center justify-end gap-3">
            <Button
              onClick={addSection}
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full border border-white/10 px-6 text-[10px] font-black tracking-[0.15em] uppercase shadow-lg transition-all active:scale-95"
            >
              <FolderOpen className="text-ring mr-2 h-4 w-4" />
              Crear Álbum
            </Button>
          </div>
        </div>

        {/* SECTION QUICK NAV */}
        {catalog.length > 0 && (
          <div className="bg-background/95 border-border/50 sticky top-0 z-30 -mx-4 border-b px-4 py-2">
            <div className="scrollbar-hide flex gap-2 overflow-x-auto">
              {catalog.map((section, idx) => (
                <button
                  key={section.id}
                  onClick={() => {
                    document
                      .getElementById(`section-${section.id}`)
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={cn(
                    'shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-black tracking-widest uppercase transition-colors',
                    section.isActive
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-secondary text-muted-foreground border-border',
                  )}
                >
                  {idx + 1}. {section.title.slice(0, 12)}
                  {section.title.length > 12 ? '…' : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* EDITOR LIST (ACCORDION) */}
        {catalog.length === 0 ? (
          <div className="bg-secondary/20 border-border/50 relative overflow-hidden rounded-[2.5rem] border py-12 text-center">
            <div className="bg-ring/5 pointer-events-none absolute top-1/2 left-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />
            <ImageIcon className="text-muted-foreground/30 relative z-10 mx-auto mb-4 h-12 w-12" />
            <p className="text-foreground relative z-10 text-sm font-black tracking-widest uppercase">
              Lienzo en Blanco
            </p>
            <p className="text-muted-foreground relative z-10 mt-1 mb-6 text-[10px] font-bold tracking-widest uppercase">
              Sube fotos de tus mejores trabajos.
            </p>

            {/* Section type preview grid */}
            <div className="relative z-10 mb-6 grid grid-cols-2 gap-3 px-6 sm:grid-cols-4">
              {[
                {
                  type: 'grid',
                  icon: LayoutGrid,
                  label: 'Cuadrícula',
                  desc: 'Muestra fotos en rejilla',
                },
                {
                  type: 'carousel',
                  icon: Images,
                  label: 'Carrusel',
                  desc: 'Desliza horizontalmente',
                },
                {
                  type: 'comparison',
                  icon: SplitSquareHorizontal,
                  label: 'Comparación',
                  desc: 'Antes y después',
                },
                {
                  type: 'links',
                  icon: Link2,
                  label: 'Enlaces',
                  desc: 'Links a redes sociales',
                },
                {
                  type: 'hub',
                  icon: Share2,
                  label: 'Hub',
                  desc: 'Directorio de enlaces',
                },
              ].map(({ type, icon: Icon, label, desc }) => (
                <button
                  key={type}
                  onClick={() => {
                    const newSection: CatalogSection = {
                      id: generateId(),
                      title: 'Nueva Sección',
                      type: type as any,
                      images: [],
                      links: [],
                      isActive: true,
                      order: catalog.length,
                      comparisonLabels: { left: 'Antes', right: 'Después' },
                    };
                    updateCatalog([...catalog, newSection]);
                  }}
                  className="border-border hover:border-ring/50 flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed p-4 transition-colors"
                >
                  <Icon className="text-muted-foreground h-8 w-8" />
                  <span className="text-xs font-black uppercase">{label}</span>
                  <span className="text-muted-foreground text-[10px]">
                    {desc}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Accordion
            type="multiple"
            defaultValue={catalog.length > 0 ? [catalog[0].id] : []}
            className="space-y-6"
          >
            {catalog.map((section, index) => {
              const activeUploadsCount = Array.from(activeUploads).filter((k) =>
                k.startsWith(`sec-${section.id}`),
              ).length;

              const leftLabel = section.comparisonLabels?.left || 'Antes';
              const rightLabel = section.comparisonLabels?.right || 'Después';

              return (
                <AccordionItem
                  key={section.id}
                  value={section.id}
                  id={`section-${section.id}`}
                  className="border-border bg-card data-[state=open]:border-ring/30 w-full rounded-[2rem] border-2 shadow-sm transition-colors"
                >
                  {/* --- SECTION HEADER PREMIUM --- */}
                  <AccordionTrigger className="border-border/50 bg-secondary/10 flex cursor-pointer flex-col items-stretch gap-4 border-b p-5 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <div className="flex w-full min-w-0 flex-1 flex-col gap-3">
                      <Input
                        value={section.title}
                        onChange={(e) =>
                          updateSection(section.id, { title: e.target.value })
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="focus-visible:border-ring h-10 w-full rounded-none border-none bg-transparent px-1 text-lg font-black tracking-tighter uppercase italic focus-visible:border-b-2 focus-visible:ring-0"
                        placeholder="NOMBRE DEL ÁLBUM"
                      />
                      <span className="text-muted-foreground text-[10px] font-black tracking-widest uppercase">
                        {section.images.length} fotos •{' '}
                        {section.type === 'grid'
                          ? 'Cuadrícula'
                          : section.type === 'carousel'
                            ? 'Carrusel'
                            : section.type === 'comparison'
                              ? 'Comparación'
                              : section.type === 'hub'
                                ? 'Hub'
                                : 'Enlaces'}
                      </span>

                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <Select
                            value={section.type}
                            onValueChange={(val: any) =>
                              updateSection(section.id, { type: val })
                            }
                          >
                            <SelectTrigger className="border-border bg-background h-10 flex-1 rounded-xl text-xs font-bold sm:max-w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border-border rounded-xl">
                              <SelectItem value="grid">
                                <div className="flex items-center gap-2 text-xs font-bold">
                                  <LayoutGrid className="text-muted-foreground h-3.5 w-3.5" />
                                  Cuadrícula
                                </div>
                              </SelectItem>
                              <SelectItem value="carousel">
                                <div className="flex items-center gap-2 text-xs font-bold">
                                  <Images className="text-muted-foreground h-3.5 w-3.5" />
                                  Carrusel
                                </div>
                              </SelectItem>
                              <SelectItem value="comparison">
                                <div className="flex items-center gap-2 text-xs font-bold">
                                  <SplitSquareHorizontal className="text-muted-foreground h-3.5 w-3.5" />
                                  Comparación
                                </div>
                              </SelectItem>
                              <SelectItem value="links">
                                <div className="flex items-center gap-2 text-xs font-bold">
                                  <Link2 className="text-muted-foreground h-3.5 w-3.5" />
                                  Enlaces
                                </div>
                              </SelectItem>
                              <SelectItem value="hub">
                                <div className="flex items-center gap-2 text-xs font-bold">
                                  <Share2 className="text-muted-foreground h-3.5 w-3.5" />
                                  Hub
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>

                          {/* Mobile Action Shortcuts */}
                          <div className="flex items-center gap-1 sm:hidden">
                            <div className="bg-background border-border flex h-10 items-center gap-2 rounded-xl border px-3">
                              <Switch
                                checked={section.isActive}
                                onCheckedChange={(checked) =>
                                  updateSection(section.id, {
                                    isActive: checked,
                                  })
                                }
                                className="data-[state=checked]:bg-ring scale-75"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:bg-destructive/10 active:bg-destructive/20 h-10 w-10 rounded-xl transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerHaptic();
                                removeSection(section.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* COMPARISON LABELS */}
                        {section.type === 'comparison' && (
                          <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                            <div className="bg-background border-border min-w-0 flex-1 space-y-1 rounded-xl border p-2">
                              <Label className="text-muted-foreground truncate px-1 text-[8px] font-black tracking-widest uppercase">
                                Izquierda
                              </Label>
                              <Input
                                value={leftLabel}
                                onChange={(e) =>
                                  updateSection(section.id, {
                                    comparisonLabels: {
                                      ...section.comparisonLabels,
                                      right: rightLabel,
                                      left: e.target.value,
                                    },
                                  })
                                }
                                className="h-6 truncate border-none bg-transparent px-1 text-xs font-bold focus-visible:ring-0"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="bg-background border-border min-w-0 flex-1 space-y-1 rounded-xl border p-2">
                              <Label className="text-muted-foreground truncate px-1 text-[8px] font-black tracking-widest uppercase">
                                Derecha
                              </Label>
                              <Input
                                value={rightLabel}
                                onChange={(e) =>
                                  updateSection(section.id, {
                                    comparisonLabels: {
                                      ...section.comparisonLabels,
                                      left: leftLabel,
                                      right: e.target.value,
                                    },
                                  })
                                }
                                className="h-6 truncate border-none bg-transparent px-1 text-xs font-bold focus-visible:ring-0"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CONTROLES DE ORDEN Y ESTADO */}
                    <div className="bg-background border-border flex w-full shrink-0 items-center justify-between gap-2 rounded-2xl border p-1.5 sm:w-auto sm:justify-end">
                      <div className="bg-secondary text-foreground active:bg-primary active:text-primary-foreground flex h-9 flex-1 items-center justify-center gap-2 truncate rounded-xl px-3 text-[9px] font-black tracking-widest uppercase transition-colors sm:flex-none">
                        <GripVertical className="text-muted-foreground mr-1 h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">Ver Fotos</span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Button
                          size="icon"
                          variant="outline"
                          className="border-border active:bg-primary active:text-primary-foreground h-9 w-9 rounded-xl transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveSection(index, 'up');
                          }}
                          disabled={index === 0}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="border-border active:bg-primary active:text-primary-foreground h-9 w-9 rounded-xl transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveSection(index, 'down');
                          }}
                          disabled={index === catalog.length - 1}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Desktop Only Extra Controls (Already covered on mobile in the selector row) */}
                      <div className="border-border ml-2 hidden items-center gap-2 border-l pl-2 sm:flex">
                        <Switch
                          checked={section.isActive}
                          onCheckedChange={(checked) =>
                            updateSection(section.id, { isActive: checked })
                          }
                          className="data-[state=checked]:bg-ring"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10 h-9 w-9 rounded-xl"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerHaptic();
                            removeSection(section.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </AccordionTrigger>

                  {/* --- ACCORDION CONTENT --- */}
                  <AccordionContent className="p-5">
                    <div
                      className={cn(
                        'transition-opacity duration-500',
                        section.isActive
                          ? 'opacity-100'
                          : 'opacity-40 grayscale',
                      )}
                    >
                      {/* TYPE: GRID */}
                      {section.type === 'grid' && (
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                          {section.images.map((img, imgIdx) => (
                            <div
                              key={img.id}
                              className="bg-secondary/50 group/item border-border relative aspect-[4/5] overflow-hidden rounded-2xl border shadow-sm"
                            >
                              {img.url && (
                                <img
                                  src={img.url}
                                  className="h-full w-full object-cover"
                                  alt={img.caption || 'Grid item'}
                                />
                              )}
                              <div className="absolute top-2 right-2 left-2">
                                <Input
                                  value={img.caption || ''}
                                  onChange={(e) =>
                                    updateImage(section.id, img.id, {
                                      caption: e.target.value || null,
                                    })
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-8 rounded-xl border-white/20 bg-black/45 px-3 text-[10px] font-bold text-white placeholder:text-white/60 focus-visible:ring-white/40"
                                  placeholder="Caption"
                                />
                              </div>

                              {/* Persistent Action Bar */}
                              <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex gap-1">
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-9 w-9 rounded-full shadow-md"
                                      onClick={() =>
                                        moveImage(section.id, imgIdx, 'left')
                                      }
                                      disabled={imgIdx === 0}
                                    >
                                      <ArrowLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-9 w-9 rounded-full shadow-md"
                                      onClick={() =>
                                        moveImage(section.id, imgIdx, 'right')
                                      }
                                      disabled={
                                        imgIdx === section.images.length - 1
                                      }
                                    >
                                      <ArrowRight className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    className="h-9 w-9 rounded-full shadow-xl"
                                    onClick={() => {
                                      triggerHaptic();
                                      removeImageSlot(
                                        section.id,
                                        img.id,
                                        'both',
                                      );
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* GRID ADD BUTTON */}
                          <label className="border-border hover:border-ring/50 bg-secondary/20 hover:bg-secondary/40 group relative col-span-2 flex aspect-[4/5] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-colors sm:col-span-1">
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, section.id)}
                            />
                            {activeUploadsCount > 0 ? (
                              <Loader2 className="text-ring mb-2 h-8 w-8 animate-spin" />
                            ) : (
                              <Plus className="text-muted-foreground group-hover:text-ring mb-1 h-8 w-8 transition-transform group-hover:scale-110" />
                            )}
                            <span className="text-foreground text-[9px] font-black uppercase">
                              {activeUploadsCount > 0
                                ? `Subiendo (${activeUploadsCount})`
                                : 'Añadir Fotos'}
                            </span>
                          </label>
                        </div>
                      )}

                      {/* TYPE: CAROUSEL */}
                      {section.type === 'carousel' && (
                        <div className="scrollbar-hide -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4">
                          {section.images.map((img, imgIdx) => (
                            <div
                              key={img.id}
                              className="bg-secondary/50 border-border group/item relative aspect-[4/5] w-[75%] shrink-0 snap-center overflow-hidden rounded-3xl border shadow-sm sm:w-[40%]"
                            >
                              {img.url && (
                                <img
                                  src={img.url}
                                  className="h-full w-full object-cover"
                                  alt={img.caption || 'Carousel item'}
                                />
                              )}
                              <div className="absolute top-3 right-3 left-3">
                                <Input
                                  value={img.caption || ''}
                                  onChange={(e) =>
                                    updateImage(section.id, img.id, {
                                      caption: e.target.value || null,
                                    })
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-9 rounded-xl border-white/20 bg-black/45 px-3 text-[10px] font-bold text-white placeholder:text-white/60 focus-visible:ring-white/40"
                                  placeholder="Caption"
                                />
                              </div>

                              {/* Persistent Action Bar */}
                              <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex gap-2">
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-10 w-10 rounded-full shadow-md"
                                      onClick={() =>
                                        moveImage(section.id, imgIdx, 'left')
                                      }
                                      disabled={imgIdx === 0}
                                    >
                                      <ArrowLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="icon"
                                      className="h-10 w-10 rounded-full shadow-md"
                                      onClick={() =>
                                        moveImage(section.id, imgIdx, 'right')
                                      }
                                      disabled={
                                        imgIdx === section.images.length - 1
                                      }
                                    >
                                      <ArrowRight className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    className="h-10 w-10 rounded-full shadow-2xl"
                                    onClick={() => {
                                      triggerHaptic();
                                      removeImageSlot(
                                        section.id,
                                        img.id,
                                        'both',
                                      );
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* CAROUSEL ADD BUTTON */}
                          <label className="border-border hover:border-ring/50 bg-secondary/20 hover:bg-secondary/40 group relative flex aspect-[4/5] w-[75%] shrink-0 cursor-pointer snap-center flex-col items-center justify-center rounded-3xl border-2 border-dashed transition-colors sm:w-[40%]">
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, section.id)}
                            />
                            {activeUploadsCount > 0 ? (
                              <Loader2 className="text-ring mb-2 h-10 w-10 animate-spin" />
                            ) : (
                              <Plus className="text-muted-foreground group-hover:text-ring mb-2 h-10 w-10 transition-transform group-hover:scale-110" />
                            )}
                            <span className="text-foreground text-[10px] font-black tracking-widest uppercase">
                              {activeUploadsCount > 0
                                ? `Subiendo (${activeUploadsCount})`
                                : 'Añadir Fotos'}
                            </span>
                          </label>
                        </div>
                      )}

                      {/* TYPE: COMPARISON */}
                      {section.type === 'comparison' && (
                        <div className="flex flex-col gap-6">
                          {section.images.map((img, imgIdx) => (
                            <div
                              key={img.id}
                              className="bg-secondary/5 border-border group/card hover:bg-secondary/10 relative flex flex-col gap-3 rounded-[3rem] border p-4 shadow-sm transition-all hover:shadow-md sm:flex-row sm:items-center sm:p-5"
                            >
                              {/* Pair Overall Controls - Always Visible */}
                              <div className="absolute -top-3 -right-2 z-20 flex gap-1">
                                <div className="bg-background ring-border/50 flex items-center rounded-full border shadow-xl ring-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 rounded-full"
                                    onClick={() =>
                                      moveImage(section.id, imgIdx, 'left')
                                    }
                                    disabled={imgIdx === 0}
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </Button>
                                  <div className="bg-border h-4 w-[1px]" />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 rounded-full"
                                    onClick={() =>
                                      moveImage(section.id, imgIdx, 'right')
                                    }
                                    disabled={
                                      imgIdx === section.images.length - 1
                                    }
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                  <div className="bg-border h-4 w-[1px]" />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:bg-destructive/10 h-9 w-9 rounded-full"
                                    onClick={() => {
                                      triggerHaptic();
                                      removeImageSlot(
                                        section.id,
                                        img.id,
                                        'both',
                                      );
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>

                              <div className="bg-background border-border sm:bg-background/90 w-full rounded-2xl border px-3 py-2 sm:absolute sm:right-5 sm:bottom-5 sm:left-5 sm:z-20 sm:w-auto sm:backdrop-blur">
                                <Label className="text-muted-foreground px-1 text-[8px] font-black tracking-widest uppercase">
                                  Caption
                                </Label>
                                <Input
                                  value={img.caption || ''}
                                  onChange={(e) =>
                                    updateImage(section.id, img.id, {
                                      caption: e.target.value || null,
                                    })
                                  }
                                  className="h-7 border-none bg-transparent px-1 text-xs font-bold focus-visible:ring-0"
                                  placeholder="Describe este resultado"
                                />
                              </div>

                              {/* SWAP BUTTON */}
                              <div className="absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 sm:static sm:translate-x-0 sm:translate-y-0">
                                <Button
                                  size="icon"
                                  className="bg-background border-card text-foreground hover:text-ring hover:bg-secondary h-12 w-12 rotate-90 rounded-full border-4 shadow-2xl transition-all sm:rotate-0"
                                  onClick={() =>
                                    swapComparisonImages(section.id, img.id)
                                  }
                                >
                                  <ArrowRightLeft className="h-5 w-5" />
                                </Button>
                              </div>

                              {/* LEFT SLOT (BeforeUrl) */}
                              <div className="border-border group/before bg-background hover:border-ring/30 relative flex aspect-[4/5] flex-1 items-center justify-center overflow-hidden rounded-[2.5rem] border-2 border-dashed shadow-inner transition-all">
                                <div className="bg-background/90 border-border absolute top-4 left-4 z-10 flex items-center gap-1.5 rounded-xl border px-3 py-1.5 shadow-xl ring-1 ring-black/5">
                                  <div className="bg-ring h-1.5 w-1.5 rounded-full" />
                                  <span className="text-foreground text-[10px] font-black tracking-[0.1em] uppercase italic">
                                    {leftLabel}
                                  </span>
                                </div>
                                {img.beforeUrl ? (
                                  <>
                                    <img
                                      src={img.beforeUrl}
                                      className="h-full w-full object-cover transition-transform duration-700 group-hover/before:scale-110"
                                      alt="Left"
                                    />
                                    {/* Tap-to-reveal overlay */}
                                    <div
                                      className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 bg-slate-950/0 transition-all group-hover/before:bg-slate-950/40"
                                      onClick={() =>
                                        setActiveImageEdit(
                                          activeImageEdit === `${img.id}-left`
                                            ? null
                                            : `${img.id}-left`,
                                        )
                                      }
                                    >
                                      {activeImageEdit === `${img.id}-left` && (
                                        <div className="animate-in zoom-in-95 flex flex-col items-center gap-3">
                                          <label className="flex cursor-pointer flex-col items-center">
                                            <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/90 shadow-2xl backdrop-blur-md transition-all hover:bg-white active:scale-95">
                                              {activeUploads.has(
                                                `${img.id}-left`,
                                              ) ? (
                                                <Loader2 className="text-ring h-6 w-6 animate-spin" />
                                              ) : (
                                                <UploadCloud className="h-6 w-6 text-slate-950" />
                                              )}
                                            </div>
                                            <span className="rounded-full bg-white/90 px-3 py-1 text-[9px] font-black tracking-widest text-slate-950 uppercase shadow-xl backdrop-blur-md">
                                              Cambiar
                                            </span>
                                            <input
                                              type="file"
                                              className="hidden"
                                              accept="image/*"
                                              onChange={(e) =>
                                                handleImageUpload(
                                                  e,
                                                  section.id,
                                                  img.id,
                                                  'left',
                                                )
                                              }
                                            />
                                          </label>
                                          <Button
                                            variant="destructive"
                                            size="sm"
                                            className="h-9 rounded-full border border-white/20 bg-rose-500/90 px-5 text-[9px] font-black tracking-widest uppercase shadow-2xl backdrop-blur-md hover:bg-rose-500 active:scale-95"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeImageSlot(
                                                section.id,
                                                img.id,
                                                'left',
                                              );
                                            }}
                                          >
                                            <Trash2 className="mr-1.5 h-3 w-3" />
                                            Borrar
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <label className="hover:bg-secondary/20 flex h-full w-full cursor-pointer flex-col items-center justify-center transition-colors">
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleImageUpload(
                                          e,
                                          section.id,
                                          img.id,
                                          'left',
                                        )
                                      }
                                    />
                                    {activeUploads.has(`${img.id}-left`) ? (
                                      <Loader2 className="text-ring h-8 w-8 animate-spin" />
                                    ) : (
                                      <div className="flex flex-col items-center gap-2 opacity-30 transition-opacity hover:opacity-100">
                                        <Plus className="text-muted-foreground h-10 w-10" />
                                        <span className="text-[9px] font-black tracking-[0.2em] uppercase">
                                          {leftLabel}
                                        </span>
                                      </div>
                                    )}
                                  </label>
                                )}
                              </div>

                              {/* RIGHT SLOT (Main Url) */}
                              <div className="border-border group/after bg-background hover:border-primary/30 relative flex aspect-[4/5] flex-1 items-center justify-center overflow-hidden rounded-[2.5rem] border-2 border-dashed shadow-inner transition-all">
                                <div className="bg-primary absolute top-4 right-4 z-10 flex items-center gap-1.5 rounded-xl border border-white/20 px-3 py-1.5 shadow-xl ring-1 ring-black/5">
                                  <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                                  <span className="text-primary-foreground text-[10px] font-black tracking-[0.1em] uppercase italic">
                                    {rightLabel}
                                  </span>
                                </div>
                                {img.url ? (
                                  <>
                                    <img
                                      src={img.url}
                                      className="h-full w-full object-cover transition-transform duration-700 group-hover/after:scale-110"
                                      alt="Right"
                                    />
                                    {/* Tap-to-reveal overlay */}
                                    <div
                                      className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 bg-slate-950/0 transition-all group-hover/after:bg-slate-950/40"
                                      onClick={() =>
                                        setActiveImageEdit(
                                          activeImageEdit === `${img.id}-right`
                                            ? null
                                            : `${img.id}-right`,
                                        )
                                      }
                                    >
                                      {activeImageEdit ===
                                        `${img.id}-right` && (
                                        <div className="animate-in zoom-in-95 flex flex-col items-center gap-3">
                                          <label className="flex cursor-pointer flex-col items-center">
                                            <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/90 shadow-2xl backdrop-blur-md transition-all hover:bg-white active:scale-95">
                                              {activeUploads.has(
                                                `${img.id}-right`,
                                              ) ? (
                                                <Loader2 className="text-primary h-6 w-6 animate-spin" />
                                              ) : (
                                                <UploadCloud className="h-6 w-6 text-slate-950" />
                                              )}
                                            </div>
                                            <span className="rounded-full bg-white/90 px-3 py-1 text-[9px] font-black tracking-widest text-slate-950 uppercase shadow-xl backdrop-blur-md">
                                              Cambiar
                                            </span>
                                            <input
                                              type="file"
                                              className="hidden"
                                              accept="image/*"
                                              onChange={(e) =>
                                                handleImageUpload(
                                                  e,
                                                  section.id,
                                                  img.id,
                                                  'right',
                                                )
                                              }
                                            />
                                          </label>
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            className="h-9 rounded-full border border-black/5 bg-white/90 px-5 text-[9px] font-black tracking-widest uppercase shadow-2xl backdrop-blur-md hover:bg-white active:scale-95"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (!img.url) return;
                                              setCropper({
                                                isOpen: true,
                                                imageSrc: img.url,
                                                aspectRatio:
                                                  IMAGE_RATIOS.PRODUCT,
                                                sectionId: section.id,
                                                targetItemId: img.id,
                                                targetSlot: 'right',
                                              });
                                            }}
                                          >
                                            <Crop className="mr-1.5 h-3.5 w-3.5 text-amber-600" />
                                            Recortar
                                          </Button>
                                          <Button
                                            variant="destructive"
                                            size="sm"
                                            className="h-9 rounded-full border border-white/20 bg-rose-500/90 px-5 text-[9px] font-black tracking-widest uppercase shadow-2xl backdrop-blur-md hover:bg-rose-500 active:scale-95"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeImageSlot(
                                                section.id,
                                                img.id,
                                                'right',
                                              );
                                            }}
                                          >
                                            <Trash2 className="mr-1.5 h-3 w-3" />
                                            Borrar
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <label className="hover:bg-secondary/20 flex h-full w-full cursor-pointer flex-col items-center justify-center transition-colors">
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleImageUpload(
                                          e,
                                          section.id,
                                          img.id,
                                          'right',
                                        )
                                      }
                                    />
                                    {activeUploads.has(`${img.id}-right`) ? (
                                      <Loader2 className="text-primary h-8 w-8 animate-spin" />
                                    ) : (
                                      <div className="flex flex-col items-center gap-2 opacity-30 transition-opacity hover:opacity-100">
                                        <Plus className="text-muted-foreground h-10 w-10" />
                                        <span className="text-[9px] font-black tracking-[0.2em] uppercase">
                                          {rightLabel}
                                        </span>
                                      </div>
                                    )}
                                  </label>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* COMPARISON ADD BUTTON */}
                          <label className="border-border hover:border-ring/50 bg-secondary/10 hover:bg-secondary/20 group flex w-full cursor-pointer flex-col items-center justify-center rounded-[2.5rem] border-2 border-dashed p-10 shadow-inner transition-colors">
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, section.id)}
                            />
                            {activeUploadsCount > 0 ? (
                              <div className="bg-background ring-border relative mb-4 flex h-16 w-16 items-center justify-center rounded-2xl shadow-xl ring-1">
                                <Loader2 className="text-ring h-8 w-8 animate-spin" />
                                <div className="bg-primary text-primary-foreground absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-black shadow-lg">
                                  {activeUploadsCount}
                                </div>
                              </div>
                            ) : (
                              <div className="bg-background ring-border mb-4 flex h-16 w-16 items-center justify-center rounded-2xl shadow-xl ring-1 transition-transform group-hover:scale-110">
                                <Plus className="text-ring h-8 w-8" />
                              </div>
                            )}
                            <span className="text-foreground text-xs font-black tracking-widest uppercase italic">
                              {activeUploadsCount > 0
                                ? 'Subiendo Galería...'
                                : 'Crear Pares'}
                            </span>
                            <p className="text-muted-foreground mt-2 max-w-[280px] text-center text-[10px] leading-relaxed font-bold uppercase">
                              Selecciona fotos intercaladas:
                              <br />
                              <span className="text-ring">
                                {leftLabel}
                              </span> →{' '}
                              <span className="text-primary">{rightLabel}</span>
                            </p>
                          </label>
                        </div>
                      )}
                      {/* TYPE: LINKS / HUB */}
                      {(section.type === 'links' || section.type === 'hub') && (
                        <div className="space-y-3">
                          {(section.links || []).map((link, lIdx) => {
                            const isInstagram =
                              link.url.includes('instagram.com');

                            return (
                              <div
                                key={link.id}
                                className="bg-secondary/20 border-border group/link relative flex flex-col gap-3 overflow-hidden rounded-2xl border p-4 sm:flex-row"
                              >
                                {/* Indicador visual de Red Social */}
                                {isInstagram && (
                                  <div className="absolute top-0 right-0 rounded-bl-xl border-b border-l border-pink-500/20 bg-pink-500/10 p-1">
                                    <Instagram className="h-3 w-3 text-pink-500" />
                                  </div>
                                )}

                                <div className="flex flex-col items-center justify-center gap-1">
                                  <GripVertical className="text-muted-foreground mb-1 h-4 w-4" />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:bg-secondary h-9 w-9 rounded-md"
                                    onClick={() =>
                                      moveLink(section.id, lIdx, 'up')
                                    }
                                    disabled={lIdx === 0}
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:bg-secondary h-9 w-9 rounded-md"
                                    onClick={() =>
                                      moveLink(section.id, lIdx, 'down')
                                    }
                                    disabled={
                                      lIdx === (section.links?.length || 0) - 1
                                    }
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                </div>

                                <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                                  <div className="space-y-1">
                                    <Label className="text-muted-foreground px-1 text-[8px] font-black tracking-widest uppercase">
                                      Etiqueta
                                    </Label>
                                    <Input
                                      value={link.label}
                                      onChange={(e) =>
                                        updateLink(section.id, link.id, {
                                          label: e.target.value,
                                        })
                                      }
                                      className="bg-background focus-visible:ring-ring h-10 border-none text-sm font-bold focus-visible:ring-1"
                                      placeholder="Ej: Ver en Instagram"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-muted-foreground px-1 text-[8px] font-black tracking-widest uppercase">
                                      URL Destino
                                    </Label>
                                    <Input
                                      value={link.url}
                                      onChange={(e) =>
                                        updateLink(section.id, link.id, {
                                          url: e.target.value,
                                        })
                                      }
                                      className="bg-background focus-visible:ring-ring h-10 border-none text-sm font-medium focus-visible:ring-1"
                                      placeholder="Pega el link aquí..."
                                    />
                                  </div>
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:bg-destructive/10 h-10 w-10 rounded-xl"
                                    onClick={() => {
                                      triggerHaptic();
                                      removeLink(section.id, link.id);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                          <Button
                            onClick={() => addLink(section.id)}
                            variant="outline"
                            className="border-border hover:border-ring/50 bg-background h-12 w-full rounded-xl border-2 border-dashed text-[10px] font-black tracking-widest uppercase"
                          >
                            <Plus className="mr-2 h-4 w-4" /> Añadir Enlace
                          </Button>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </main>

      {isDirty && (
        <div className="fixed right-0 bottom-4 left-0 z-50 px-4 lg:left-(--sidebar-width,0px)">
          <div className="mx-auto flex max-w-md items-center gap-3 rounded-[1.75rem] border border-white/20 bg-zinc-950/95 p-2 shadow-2xl backdrop-blur-xl">
            <div className="min-w-0 flex-1 px-3">
              <p className="truncate text-[9px] font-black tracking-widest text-zinc-400 uppercase">
                Cambios sin publicar
              </p>
              {isUploadBusy && (
                <p className="truncate text-[8px] font-bold tracking-widest text-amber-300 uppercase">
                  Espera a que termine la subida
                </p>
              )}
            </div>
            <Button
              type="button"
              onClick={handleSaveAll}
              disabled={isSaving}
              className="h-12 shrink-0 rounded-[1.25rem] bg-white px-6 text-[11px] font-black tracking-widest text-zinc-950 uppercase hover:bg-zinc-100"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Publicar'
              )}
            </Button>
          </div>
        </div>
      )}

      <ImageCropperModal
        isOpen={cropper.isOpen}
        imageSrc={cropper.imageSrc}
        aspectRatio={cropper.aspectRatio}
        onCropComplete={onCropComplete}
        onClose={() => {
          setCropper((prev) => ({ ...prev, isOpen: false }));
          setCroppingQueue([]);
          setTotalToCrop(0);
        }}
        title={
          totalToCrop > 1
            ? `Recortar ${totalToCrop - croppingQueue.length + 1} de ${totalToCrop}`
            : undefined
        }
      />

      {/* COMPONENTE GUARDIA */}
      <FormGuard
        isDirty={isDirty}
        isSubmitting={isSaving}
        onSave={handleSaveAll}
        saveLabel="Publicar Catálogo"
        isHidden
      />
    </div>
  );
}
